// chat/actions.ts — the executable action layer for the in-app assistant.
// Every action runs ONLY against THIS app's own on-device store (collections/items/sets)
// and the app's EXISTING brain (runAction). No new endpoints, no expense-domain leftovers.
// The domain here is ITEMS inside the currently-open collection.
import { store, todayLocal, formatValue, type Item, type ItemPatch,
  type CollectionPatch, type NewSet, type SetPatch, type NewProvenanceEvent } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "curio";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave, researchItem } from "../brain";
import { addInstruction, findCollectionByName } from "./brain";
import { runAction, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

/** Item-list filter over the open collection. */
export interface ItemFilter {
  /** free-text match over name / source / condition / attribute values */
  text?: string;
  /** have (owned) vs want (wishlist) vs everything */
  status?: "have" | "want" | "all";
}

/** A compact row shown inline in the chat after a filter/search. */
export interface FilterRow { id: string; name: string; value: string; status: string; source: string; }

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_item"; name: string; cost: number | null; wishlist: boolean; collectionId?: string; collectionName?: string }
  | { kind: "edit_item"; id: string; patch: ItemPatch; label?: string }
  | { kind: "delete_item"; id: string; label?: string }
  | { kind: "filter"; filter: ItemFilter; label?: string }
  | { kind: "summarize" }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "research_item"; id: string; itemName: string; collectionKind?: string; attrs?: Record<string, unknown> }
  | { kind: "create_collection"; name: string; collectionKind?: string }
  | { kind: "edit_collection"; id: string; patch: CollectionPatch; label?: string }
  | { kind: "delete_collection"; id: string; label?: string }
  | { kind: "move_item"; id: string; itemLabel?: string; toCollectionId: string; toLabel?: string }
  // ── sets (the completeness groups inside a collection) ──
  | { kind: "add_set"; input: NewSet; label?: string }
  | { kind: "edit_set"; id: string; patch: SetPatch; label?: string }
  | { kind: "delete_set"; id: string; label?: string } // UI confirms
  // ── provenance events on ONE item (its logged history) ──
  | { kind: "add_provenance"; input: NewProvenanceEvent; label?: string }
  | { kind: "delete_provenance"; id: string; label?: string } // UI confirms
  | { kind: "suggest_collections" }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult { ok: boolean; message: string; changed?: boolean; rows?: FilterRow[]; }

function toRow(it: Item): FilterRow {
  return {
    id: it.id,
    name: it.name,
    value: it.cost != null ? formatValue(it.cost) : "—",
    status: it.wishlist ? "Wishlist" : "Have",
    source: it.source || "",
  };
}

/** Pure, testable filter used by both the router (to disambiguate) and the executor. */
export function applyItemFilter(items: Item[], f: ItemFilter): Item[] {
  let rows = items;
  if (f.status === "have") rows = rows.filter((i) => !i.wishlist);
  else if (f.status === "want") rows = rows.filter((i) => i.wishlist);
  const q = (f.text ?? "").trim().toLowerCase();
  if (q) {
    rows = rows.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        (i.source ?? "").toLowerCase().includes(q) ||
        (i.condition ?? "").toLowerCase().includes(q) ||
        Object.values(i.attrs ?? {}).some((v) => v != null && String(v).toLowerCase().includes(q)),
    );
  }
  return rows;
}

/** Run one resolved action against the open collection (`collectionId`). */
export async function executeAction(a: ChatAction, collectionId: string | null, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };
      case "add_item": {
        let cid = a.collectionId ?? collectionId;
        // "create the coins collection and add my quarter" in ONE message: the collection doesn't
        // exist while actions are being RESOLVED, only after the create has EXECUTED. So a named
        // target resolves here, at execution time, when its sibling create has already run
        // (found live 2026-08-27: the item silently vanished with "Couldn't find a collection").
        if (!cid && a.collectionName) {
          const cols = await findCollectionByName(a.collectionName);
          if (cols.length === 1) cid = cols[0].id;
        }
        if (!cid) return { ok: false, message: a.collectionName ? `Couldn't find a collection called "${a.collectionName}".` : "Open a collection first, or tell me which collection to add it to." };
        const it = await store.createItem({ collection_id: cid, name: a.name, cost: a.cost, wishlist: a.wishlist });
        return { ok: true, message: `Added ${it.name}${a.wishlist ? " to your wishlist" : ""}.`, changed: true };
      }
      case "edit_item":
        await store.updateItem(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the item"}.`, changed: true };
      case "delete_item":
        await store.deleteItem(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the item"}.`, changed: true };
      case "filter": {
        const all = collectionId ? await store.listItems(collectionId) : await store.listItems();
        const rows = applyItemFilter(all, a.filter);
        // A bare "No matches." is a dead end (measured live 2026-08-19: a natural question the
        // placeholder itself invites got exactly that). Name what IS here — local data rendered
        // locally, nothing leaves the device — so the customer has somewhere to go next.
        let message: string;
        if (rows.length === 0) {
          const names = all.slice(0, 3).map((i) => i.name).join(", ");
          message = all.length === 0
            ? "Nothing logged here yet — add your first item and I'll have something to search."
            : `No matches. You have ${all.length} item${all.length === 1 ? "" : "s"} here${names ? ` — ${names}${all.length > 3 ? ", …" : ""}` : ""}.`;
        } else {
          message = `${rows.length} ${rows.length === 1 ? "match" : "matches"}`;
        }
        return { ok: true, message, rows: rows.slice(0, 50).map(toRow) };
      }
      case "summarize": {
        if (!collectionId) {
          // HOME overview — deterministic, no AI call: narrate the collections.
          const cols = await store.listCollections();
          if (!cols.length) return { ok: true, message: "You don't have any collections yet — tell me what you collect and I'll set one up." };
          const lines = await Promise.all(cols.map(async (c) => {
            const n = (await store.listItems(c.id)).length;
            return `• ${c.name} — ${n} item${n === 1 ? "" : "s"}${c.kind ? ` (${c.kind})` : ""}`;
          }));
          const total = (await store.listItems()).length;
          return { ok: true, message: `You have ${cols.length} collection${cols.length === 1 ? "" : "s"}, ${total} item${total === 1 ? "" : "s"} in total:\n\n${lines.join("\n")}` };
        }
        // Reuse the app's EXISTING brain: compute the on-device signal slice, narrate a recap.
        const signals = await store.computeSignals(collectionId, todayLocal());
        const r = await runAction("recap", signals);
        return { ok: true, message: r.text };
      }
      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // Photo-heavy backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup is photo-heavy, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path (item photos add up). One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size, item photos included.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large photo-heavy backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my collection" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large (it can be, with item photos), use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "research_item": {
        // Tier-B framed research → appended onto the item's notes (never overwrites the user's own notes).
        const { text } = await researchItem({ name: a.itemName, kind: a.collectionKind, attrs: a.attrs });
        const cur = await store.getItem(a.id);
        const stamp = todayLocal();
        const block = `— AI research (${stamp}) —\n${text}`;
        const notes = cur?.notes && cur.notes.trim() ? `${cur.notes.trim()}\n\n${block}` : block;
        await store.updateItem(a.id, { notes });
        return { ok: true, message: `${text}\n\n(Saved to ${a.itemName}'s notes.)`, changed: true };
      }

      case "create_collection": {
        const col = await store.createCollection({ name: a.name, kind: a.collectionKind || undefined });
        return { ok: true, message: `Created the "${col.name}" collection — open it to start adding items.`, changed: true };
      }

      case "delete_collection": {
        await store.deleteCollection(a.id);
        return { ok: true, message: `Deleted the "${a.label ?? "collection"}" collection and everything in it.`, changed: true };
      }

      case "move_item": {
        // Sets are per-collection, so the Rust update clears set_id on a move.
        await store.updateItem(a.id, { collection_id: a.toCollectionId });
        return { ok: true, message: `Moved ${a.itemLabel ?? "the item"} to ${a.toLabel ?? "the other collection"}.`, changed: true };
      }

      case "edit_collection":
        await store.updateCollection(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the collection"}.`, changed: true };

      // ── sets ──
      case "add_set": {
        const s = await store.createSet(a.input);
        return { ok: true, message: `Added the "${s.name}" set${a.label ? ` to ${a.label}` : ""}.`, changed: true };
      }
      case "edit_set":
        await store.updateSet(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the set"}.`, changed: true };
      case "delete_set":
        await store.deleteSet(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the set"}.`, changed: true };

      // ── provenance events ──
      case "add_provenance": {
        await store.createProvenanceEvent(a.input);
        return { ok: true, message: `Logged a provenance event${a.label ? ` for ${a.label}` : ""}.`, changed: true };
      }
      case "delete_provenance":
        await store.deleteProvenanceEvent(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the provenance event"}.`, changed: true };

      case "suggest_collections": {
        // Recommend starter collections, skipping kinds the user already keeps.
        const existing = (await store.listCollections()).map((c) => `${c.kind ?? ""} ${c.name ?? ""}`.toLowerCase());
        const ideas = ["Coins", "Vinyl records", "Trading cards", "Rocks & minerals", "Stamps", "Watches", "Books", "Sneakers", "Comics", "Wine", "Art prints", "LEGO sets"];
        const fresh = ideas.filter((i) => { const w = i.toLowerCase().split(" ")[0]; return !existing.some((e) => e.includes(w)); });
        const pick = (fresh.length ? fresh : ideas).slice(0, 8);
        return { ok: true, message: `Collections people love to start:\n\n• ${pick.join("\n• ")}\n\nJust say the word — e.g. "start a coins collection" — and I'll set it up with sensible fields.` };
      }

      case "save_to_cloud": {
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
